import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { safeStorage } from '@/utils';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '';

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = safeStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle 401 refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = safeStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token } = response.data;
          safeStorage.setItem('access_token', access_token);
          safeStorage.setItem('refresh_token', refresh_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch {
          safeStorage.removeItem('access_token');
          safeStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        safeStorage.removeItem('access_token');
        safeStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);

// --- Auth API ---
export const authApi = {
  login: (username: string, password: string) =>
    apiClient.post('/auth/login', { username, password }),
  register: (data: { username: string; email: string; password: string }) =>
    apiClient.post('/auth/register', data),
  me: () => apiClient.get('/auth/me'),
  refresh: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refresh_token: refreshToken }),
};

// --- Overview API ---
export const overviewApi = {
  get: () => apiClient.get('/overview'),
};

// --- Portfolio API ---
export const portfolioApi = {
  snapshot: (accountId?: string) =>
    apiClient.get('/portfolio/snapshot', { params: { account_id: accountId } }),
  equityCurve: (accountId?: string, days?: number) =>
    apiClient.get('/portfolio/equity-curve', { params: { account_id: accountId, days } }),
  positions: (accountId?: string) =>
    apiClient.get('/portfolio/positions', { params: { account_id: accountId } }),
};

// --- Trades API ---
export const tradesApi = {
  list: (filters?: Record<string, unknown>) =>
    apiClient.get('/trades', { params: filters }),
  submit: (data: {
    symbol: string;
    side: string;
    quantity: number;
    account_id: string;
    price?: number;
  }) => apiClient.post('/trades/manual', data),
};

// --- Settings API ---
export const settingsApi = {
  list: () => apiClient.get('/settings'),
  get: (key: string) => apiClient.get(`/settings/${key}`),
  update: (settings: { key: string; value: string }[]) =>
    apiClient.put('/settings', { settings }),
  rollback: (auditLogId: string) =>
    apiClient.post(`/settings/rollback/${auditLogId}`),
  auditLogs: (params?: Record<string, unknown>) =>
    apiClient.get('/settings/audit', { params }),
};

// --- Analytics API ---
export const analyticsApi = {
  summary: (accountId?: string, days?: number) =>
    apiClient.get('/analytics/summary', { params: { account_id: accountId, days } }),
  dailyPnl: (accountId?: string, days?: number) =>
    apiClient.get('/analytics/daily-pnl', { params: { account_id: accountId, days } }),
  symbolBreakdown: (accountId?: string) =>
    apiClient.get('/analytics/symbol-breakdown', { params: { account_id: accountId } }),
  strategyBreakdown: (accountId?: string) =>
    apiClient.get('/analytics/strategy-breakdown', { params: { account_id: accountId } }),
};

// --- Accounts API ---
export const accountsApi = {
  list: () => apiClient.get('/accounts'),
  get: (id: string) => apiClient.get(`/accounts/${id}`),
  create: (data: {
    name: string;
    description: string;
    initial_balance: number;
    risk_percentage: number;
    mode: string;
  }) => apiClient.post('/accounts', data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.put(`/accounts/${id}`, data),
  delete: (id: string) => apiClient.delete(`/accounts/${id}`),
};

// --- Exchange API ---
export const exchangeApi = {
  list: () => apiClient.get('/exchange'),
  connect: (data: {
    name: string;
    exchange_type: string;
    api_key: string;
    api_secret: string;
    passphrase?: string;
  }) => apiClient.post('/exchange/connect', data),
  disconnect: (id: string) => apiClient.post(`/exchange/${id}/disconnect`),
  validate: (id: string) => apiClient.post(`/exchange/${id}/validate`),
  activateLive: (data: {
    exchange_id: string;
    confirmation_phrase: string;
    risk_acknowledgement: boolean;
  }) => apiClient.post('/exchange/activate-live', data),
  shutdownStatus: () => apiClient.get('/exchange/emergency-shutdown'),
  activateShutdown: (reason: string) =>
    apiClient.post('/exchange/emergency-shutdown/activate', { reason }),
  deactivateShutdown: () =>
    apiClient.post('/exchange/emergency-shutdown/deactivate'),
};

// --- AI API ---
export const aiApi = {
  analyze: (symbol: string, provider?: string) =>
    apiClient.post('/ai/analyze', { symbol, provider }),
  providers: () => apiClient.get('/ai/providers'),
  decisions: (params?: Record<string, unknown>) =>
    apiClient.get('/ai/decisions', { params }),
};

// --- Market Data API ---
export const marketApi = {
  prices: (symbols?: string) =>
    apiClient.get('/market/prices', { params: { symbols } }),
  ticker: (symbol: string) =>
    apiClient.get(`/market/ticker/${symbol.replace('/', '-')}`),
  overview: () => apiClient.get('/market/overview'),
  candles: (symbol: string, days?: number) =>
    apiClient.get(`/market/candles/${symbol.replace('/', '-')}`, { params: { days } }),
};

// --- Crypto.com API ---
export const cryptocomApi = {
  status: () => apiClient.get('/cryptocom/status'),
  balances: (scope?: string) =>
    apiClient.get('/cryptocom/balances', { params: { scope: scope || 'all' } }),
  balance: (symbol: string) => apiClient.get(`/cryptocom/balance/${symbol}`),
  tradingLimit: () => apiClient.get('/cryptocom/trading-limit'),
  quote: (data: { from_symbol: string; to_symbol: string; side: string; amount: number }) =>
    apiClient.post('/cryptocom/quote', data),
  confirm: (data: { quote_id: string }) =>
    apiClient.post('/cryptocom/confirm', data),
  history: () => apiClient.get('/cryptocom/history'),
  coinSearch: (query: string) =>
    apiClient.get('/cryptocom/coins/search', { params: { q: query } }),
  cashDiscover: () => apiClient.get('/cryptocom/cash/discover'),
  paymentNetworks: (currency: string) =>
    apiClient.get(`/cryptocom/cash/payment-networks/${currency}`),
  depositMethods: (currency: string, method: string) =>
    apiClient.get(`/cryptocom/cash/deposit-methods/${currency}/${method}`),
  bankAccounts: () => apiClient.get('/cryptocom/cash/bank-accounts'),
  revokeKey: () => apiClient.post('/cryptocom/revoke-key'),
};

// --- Paper Trade API ---
export const paperTradeApi = {
  analyze: (data: { account_id: string; symbol: string; dollar_amount: number }) =>
    apiClient.post('/paper-trade/analyze', data),
  execute: (data: { account_id: string; symbol: string; side: string; dollar_amount: number; price: number }) =>
    apiClient.post('/paper-trade/execute', data),
  history: (accountId: string, params?: { page?: number; page_size?: number }) =>
    apiClient.get(`/paper-trade/history/${accountId}`, { params }),
  portfolio: (accountId: string) =>
    apiClient.get(`/paper-trade/portfolio/${accountId}`),
  reset: (accountId: string) =>
    apiClient.post(`/paper-trade/reset/${accountId}`),
};


// --- Notifications API ---
export const notificationsApi = {
  getConfig: () => apiClient.get('/notifications/config'),
  updateConfig: (data: Record<string, unknown>) =>
    apiClient.put('/notifications/config', data),
  test: (channel: string) => apiClient.post('/notifications/test', { channel }),
  history: (params?: Record<string, unknown>) =>
    apiClient.get('/notifications/history', { params }),
};

// --- Reports API ---
export const reportsApi = {
  generate: (data: {
    type: string;
    start_date?: string;
    end_date?: string;
    format?: string;
  }) => apiClient.post('/reports/generate', data),
  evaluation: (period: string) =>
    apiClient.get('/reports/evaluation', { params: { period } }),
  history: (params?: Record<string, unknown>) =>
    apiClient.get('/reports/history', { params }),
};

// --- Risk API ---
export const riskApi = {
  shutdownStatus: () => apiClient.get('/risk/shutdown/status'),
  activateShutdown: (reason: string) =>
    apiClient.post('/risk/shutdown/activate', { reason }),
  deactivateShutdown: (confirm: boolean) =>
    apiClient.post('/risk/shutdown/deactivate', { confirm }),
  riskStatus: () => apiClient.get('/risk/status'),
  events: (params?: { account_id?: string; severity?: string; page?: number; page_size?: number }) =>
    apiClient.get('/risk/events', { params }),
  accountConfig: (accountId: string) =>
    apiClient.get(`/risk/config/${accountId}`),
};

// --- Health API ---
export const healthApi = {
  get: () => apiClient.get('/health'),
};

// --- Logs API ---
export const logsApi = {
  query: (params?: Record<string, unknown>) =>
    apiClient.get('/logs', { params }),
  userActivity: (params?: Record<string, unknown>) =>
    apiClient.get('/logs/activity', { params }),
};

export default apiClient;
